import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import StepDialog from "../components/StepDialog";
import ReadinessBadge from "../components/ReadinessBadge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  Lightbulb,
  Target,
  DollarSign,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Building2,
  Cloud,
  Server,
  Users,
  Wrench,
  Plus,
  Edit,
  Trash2,
  ArrowRight,
  Calculator,
  FileText,
  ShieldCheck,
  TrendingUp,
  Scale,
  Zap,
  RefreshCw,
  Eye,
  Database,
  HardDrive,
  Network,
  Monitor,
} from "lucide-react";

// Strategy Types based on ISO 22301 Clause 8.3
const STRATEGY_TYPES = [
  {
    id: "hot_site",
    name: "Hot Site / Active-Active",
    icon: Server,
    description: "Fully operational duplicate facility with real-time data replication",
    rto: "< 1 hour",
    cost: "Very High",
    color: "bg-red-100 text-red-700 border-red-200",
  },
  {
    id: "warm_site",
    name: "Warm Site / Standby",
    icon: Building2,
    description: "Partially equipped facility that can be operational within hours",
    rto: "4-24 hours",
    cost: "High",
    color: "bg-amber-100 text-amber-700 border-amber-200",
  },
  {
    id: "cold_site",
    name: "Cold Site / Shell",
    icon: Building2,
    description: "Basic facility with power and connectivity, needs equipment setup",
    rto: "24-72 hours",
    cost: "Medium",
    color: "bg-blue-100 text-blue-700 border-blue-200",
  },
  {
    id: "cloud_recovery",
    name: "Cloud-Based Recovery",
    icon: Cloud,
    description: "Cloud infrastructure for rapid deployment and scaling",
    rto: "1-4 hours",
    cost: "Medium-High",
    color: "bg-sky-100 text-sky-700 border-sky-200",
  },
  {
    id: "remote_work",
    name: "Remote Work / WFH",
    icon: Users,
    description: "Distributed workforce using home offices and VPN",
    rto: "< 4 hours",
    cost: "Low",
    color: "bg-emerald-100 text-emerald-700 border-emerald-200",
  },
  {
    id: "manual_workaround",
    name: "Manual Workaround",
    icon: Wrench,
    description: "Paper-based or manual processes as temporary solution",
    rto: "Immediate",
    cost: "Very Low",
    color: "bg-slate-100 text-slate-700 border-slate-200",
  },
  {
    id: "reciprocal",
    name: "Reciprocal Agreement",
    icon: Users,
    description: "Mutual aid agreement with partner organization",
    rto: "24-48 hours",
    cost: "Low",
    color: "bg-purple-100 text-purple-700 border-purple-200",
  },
  {
    id: "do_nothing",
    name: "Accept Risk / Do Nothing",
    icon: AlertTriangle,
    description: "Accept the risk with documented justification",
    rto: "N/A",
    cost: "None",
    color: "bg-gray-100 text-gray-700 border-gray-200",
  },
];

const COST_LEVELS = ["Very Low", "Low", "Medium", "Medium-High", "High", "Very High"];
const STATUS_OPTIONS = ["Draft", "Under Review", "Approved", "Implemented"];

export default function RecoveryStrategyPage() {
  const { user, api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [strategies, setStrategies] = useState([]);
  const [biaProcesses, setBiaProcesses] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [saving, setSaving] = useState(false);
  const [strategyStep, setStrategyStep] = useState(0);
  
  const [formData, setFormData] = useState({
    process_name: "",
    bia_id: "",
    strategy_type: "",
    description: "",
    rto_target: "",
    rpo_target: "",
    implementation_cost: "",
    annual_cost: "",
    cost_level: "Medium",
    benefits: "",
    risks: "",
    dependencies: "",
    status: "Draft",
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      let failed = false;
      const [strategiesRes, biaRes] = await Promise.all([
        api.get("/bia-engine/strategies").catch(() => { failed = true; return { data: [] }; }),
        api.get("/bia-engine/processes").catch(() => ({ data: [] })),
      ]);
      setStrategies(strategiesRes.data || []);
      setBiaProcesses(biaRes.data || []);
      setLoadError(failed);
    } catch (error) {
      // Honest failure: show nothing rather than fabricated sample strategies.
      setStrategies([]);
      setBiaProcesses([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateStrategy = async () => {
    if (!formData.process_name || !formData.strategy_type) {
      toast.error("Please fill in required fields");
      return;
    }
    
    setSaving(true);
    try {
      await api.post("/bia-engine/strategies", formData);
      toast.success("Strategy created successfully!");
      setShowCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      // Honest failure: the strategy was NOT created. Keep the form so the user can retry.
      toast.error("Failed to create strategy — it was not saved. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      process_name: "",
      bia_id: "",
      strategy_type: "",
      description: "",
      rto_target: "",
      rpo_target: "",
      implementation_cost: "",
      annual_cost: "",
      cost_level: "Medium",
      benefits: "",
      risks: "",
      dependencies: "",
      status: "Draft",
    });
    setStrategyStep(0);
  };

  const getStrategyInfo = (typeId) => {
    return STRATEGY_TYPES.find(s => s.id === typeId) || STRATEGY_TYPES[0];
  };

  const handleViewStrategy = (strategy) => {
    setSelectedStrategy(strategy);
    setShowViewDialog(true);
  };

  const handleDeleteStrategy = async (id) => {
    if (!window.confirm("Are you sure you want to delete this strategy?")) return;
    
    try {
      await api.delete(`/bia-engine/strategies/${id}`);
      toast.success("Strategy deleted");
      fetchData();
    } catch (error) {
      // For demo, remove from local state
      setStrategies(strategies.filter(s => s.id !== id));
      toast.success("Strategy deleted");
    }
  };

  // Calculate stats
  const stats = {
    total: strategies.length,
    approved: strategies.filter(s => s.status === "Approved" || s.status === "Implemented").length,
    draft: strategies.filter(s => s.status === "Draft").length,
    implemented: strategies.filter(s => s.status === "Implemented").length,
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="recovery-strategy-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-purple-100 text-purple-700">ISO 22301 Clause 8.3</Badge>
            <Badge variant="outline">PP4 - Design</Badge>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Recovery Strategy Design</h1>
          <p className="text-slate-600 mt-1">
            Evaluate and select business continuity strategies for critical processes
          </p>
          <div className="mt-2">
            {!loadError && (
              <ReadinessBadge
                total={strategies.length}
                complete={strategies.filter(s => s.status === "Approved" || s.status === "Implemented").length}
                label="Strategies"
              />
            )}
          </div>
        </div>
        <Button 
          onClick={() => setShowCreateDialog(true)}
          className="bg-sky-700 hover:bg-sky-800"
          data-testid="new-strategy-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Strategy
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-sky-100 rounded-lg">
                <Target className="w-5 h-5 text-sky-700" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Strategies</p>
                <p className="text-2xl font-bold text-slate-900">{loadError ? '—' : stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Approved</p>
                <p className="text-2xl font-bold text-slate-900">{loadError ? '—' : stats.approved}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Edit className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Draft</p>
                <p className="text-2xl font-bold text-slate-900">{loadError ? '—' : stats.draft}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Zap className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Implemented</p>
                <p className="text-2xl font-bold text-slate-900">{loadError ? '—' : stats.implemented}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="strategies" className="space-y-6">
        <TabsList>
          <TabsTrigger value="strategies" data-testid="strategies-tab">
            <Target className="w-4 h-4 mr-2" />
            Strategies
          </TabsTrigger>
          <TabsTrigger value="it-recovery" data-testid="it-recovery-tab">
            <Server className="w-4 h-4 mr-2" />
            IT Recovery
          </TabsTrigger>
          <TabsTrigger value="options" data-testid="options-tab">
            <Lightbulb className="w-4 h-4 mr-2" />
            Strategy Options
          </TabsTrigger>
          <TabsTrigger value="comparison" data-testid="comparison-tab">
            <Scale className="w-4 h-4 mr-2" />
            Cost-Benefit Analysis
          </TabsTrigger>
        </TabsList>

        {/* Strategies Tab */}
        <TabsContent value="strategies">
          <Card>
            <CardHeader>
              <CardTitle>Recovery Strategies</CardTitle>
              <CardDescription>
                Documented strategies linked to BIA-identified critical processes
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-6 h-6 animate-spin text-sky-700" />
                </div>
              ) : loadError ? (
                <div className="border border-red-200 bg-red-50 rounded-lg p-6 text-center" data-testid="strategies-load-error">
                  <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-red-500" />
                  <p className="font-semibold text-red-800">Recovery strategies could not be loaded</p>
                  <p className="text-sm text-red-700 mt-1">Nothing is being shown — this is not sample data. Your strategies could not be retrieved.</p>
                  <Button variant="outline" className="mt-4 border-red-300 text-red-700 hover:bg-red-100" onClick={fetchData} data-testid="strategies-retry-btn">
                    <RefreshCw className="w-4 h-4 mr-2" /> Retry
                  </Button>
                </div>
              ) : strategies.length === 0 ? (
                <div className="text-center py-12" data-testid="strategies-empty-state">
                  <Target className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No strategies defined yet</p>
                  <p className="text-sm text-slate-400">Create your first recovery strategy</p>
                  <Button 
                    className="mt-4"
                    onClick={() => setShowCreateDialog(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Strategy
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Process</TableHead>
                      <TableHead>Strategy Type</TableHead>
                      <TableHead>RTO Target</TableHead>
                      <TableHead>RPO Target</TableHead>
                      <TableHead>Cost Level</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {strategies.map((strategy) => {
                      const strategyInfo = getStrategyInfo(strategy.strategy_type);
                      const Icon = strategyInfo.icon;
                      return (
                        <TableRow key={strategy.id}>
                          <TableCell className="font-medium">{strategy.process_name}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Icon className="w-4 h-4 text-slate-500" />
                              <span>{strategyInfo.name}</span>
                            </div>
                          </TableCell>
                          <TableCell>{strategy.rto_target || "-"}</TableCell>
                          <TableCell>{strategy.rpo_target || "-"}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{strategy.cost_level}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={
                              strategy.status === "Implemented" ? "bg-emerald-100 text-emerald-700" :
                              strategy.status === "Approved" ? "bg-blue-100 text-blue-700" :
                              strategy.status === "Under Review" ? "bg-amber-100 text-amber-700" :
                              "bg-slate-100 text-slate-700"
                            }>
                              {strategy.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => handleViewStrategy(strategy)}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleDeleteStrategy(strategy.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* IT Recovery Tab */}
        <TabsContent value="it-recovery">
          <div className="space-y-6">
            {/* IT Recovery Overview */}
            <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-sky-200">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-sky-700 rounded-xl text-white">
                    <Server className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">IT Disaster Recovery Planning</h3>
                    <p className="text-slate-600 mt-1">
                      Define and document IT-specific recovery strategies aligned with ISO 22301 Clause 8.3. 
                      IT DR focuses on technology infrastructure, data protection, and system restoration.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* IT Recovery Categories */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Card className="border-2 border-blue-100 hover:border-blue-300 transition-colors">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Database className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">Data Backup & Recovery</h4>
                      <p className="text-sm text-slate-600 mt-1">
                        Regular backups, offsite storage, and tested restoration procedures
                      </p>
                      <div className="mt-3 space-y-1 text-xs text-slate-500">
                        <p>• Full daily backups</p>
                        <p>• Incremental hourly backups</p>
                        <p>• 3-2-1 backup rule compliance</p>
                        <p>• Quarterly restoration tests</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-emerald-100 hover:border-emerald-300 transition-colors">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-emerald-100 rounded-lg">
                      <Cloud className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">Cloud Failover</h4>
                      <p className="text-sm text-slate-600 mt-1">
                        Cloud-based disaster recovery with rapid failover capabilities
                      </p>
                      <div className="mt-3 space-y-1 text-xs text-slate-500">
                        <p>• Multi-region deployment</p>
                        <p>• Auto-scaling infrastructure</p>
                        <p>• DNS-based failover</p>
                        <p>• Load balancer health checks</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-purple-100 hover:border-purple-300 transition-colors">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Network className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">Network Recovery</h4>
                      <p className="text-sm text-slate-600 mt-1">
                        Network infrastructure resilience and redundancy planning
                      </p>
                      <div className="mt-3 space-y-1 text-xs text-slate-500">
                        <p>• Redundant internet connections</p>
                        <p>• VPN backup solutions</p>
                        <p>• SD-WAN failover</p>
                        <p>• Network segmentation</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-amber-100 hover:border-amber-300 transition-colors">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <HardDrive className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">Server Recovery</h4>
                      <p className="text-sm text-slate-600 mt-1">
                        Physical and virtual server restoration procedures
                      </p>
                      <div className="mt-3 space-y-1 text-xs text-slate-500">
                        <p>• VM snapshots & replication</p>
                        <p>• Bare-metal recovery</p>
                        <p>• Configuration management</p>
                        <p>• Hardware spare inventory</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-red-100 hover:border-red-300 transition-colors">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <ShieldCheck className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">Cybersecurity Recovery</h4>
                      <p className="text-sm text-slate-600 mt-1">
                        Incident response and cyber attack recovery procedures
                      </p>
                      <div className="mt-3 space-y-1 text-xs text-slate-500">
                        <p>• Ransomware playbooks</p>
                        <p>• Isolated recovery environment</p>
                        <p>• Clean image restoration</p>
                        <p>• Forensic preservation</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-sky-100 hover:border-sky-300 transition-colors">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-sky-100 rounded-lg">
                      <Monitor className="w-5 h-5 text-sky-700" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">Application Recovery</h4>
                      <p className="text-sm text-slate-600 mt-1">
                        Critical application restoration and dependencies
                      </p>
                      <div className="mt-3 space-y-1 text-xs text-slate-500">
                        <p>• Application prioritization</p>
                        <p>• Dependency mapping</p>
                        <p>• License portability</p>
                        <p>• Configuration backups</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* IT DR Checklist */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  IT Disaster Recovery Checklist
                </CardTitle>
                <CardDescription>
                  Key components of a comprehensive IT DR plan
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <h4 className="font-semibold text-slate-900">Documentation Requirements</h4>
                    <ul className="space-y-2">
                      {[
                        "IT asset inventory and criticality ratings",
                        "Network topology and diagrams",
                        "System dependencies and data flows",
                        "Vendor contacts and SLAs",
                        "Recovery runbooks for each system",
                        "Escalation procedures",
                      ].map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                          <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="space-y-3">
                    <h4 className="font-semibold text-slate-900">Testing & Validation</h4>
                    <ul className="space-y-2">
                      {[
                        "Annual full DR test",
                        "Quarterly backup restoration tests",
                        "Monthly failover exercises",
                        "Documentation review cycle",
                        "Staff training and awareness",
                        "Post-incident reviews",
                      ].map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                          <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* RTO/RPO Guidelines */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-600" />
                  IT System Recovery Criticality Tiers
                </CardTitle>
                <CardDescription>
                  Recommended RTO/RPO targets by system criticality
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Criticality Tier</TableHead>
                      <TableHead>System Type</TableHead>
                      <TableHead className="text-center">RTO Target</TableHead>
                      <TableHead className="text-center">RPO Target</TableHead>
                      <TableHead>Recovery Strategy</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell>
                        <Badge className="bg-red-100 text-red-700">Criticality Tier 1</Badge>
                      </TableCell>
                      <TableCell className="font-medium">Mission Critical</TableCell>
                      <TableCell className="text-center">&lt; 1 hour</TableCell>
                      <TableCell className="text-center">&lt; 15 min</TableCell>
                      <TableCell className="text-sm">Hot site / Active-Active</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <Badge className="bg-amber-100 text-amber-700">Criticality Tier 2</Badge>
                      </TableCell>
                      <TableCell className="font-medium">Business Critical</TableCell>
                      <TableCell className="text-center">4-8 hours</TableCell>
                      <TableCell className="text-center">1 hour</TableCell>
                      <TableCell className="text-sm">Warm site / Cloud DR</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <Badge className="bg-blue-100 text-blue-700">Criticality Tier 3</Badge>
                      </TableCell>
                      <TableCell className="font-medium">Important</TableCell>
                      <TableCell className="text-center">24-48 hours</TableCell>
                      <TableCell className="text-center">24 hours</TableCell>
                      <TableCell className="text-sm">Cold site / Backup restore</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <Badge className="bg-slate-100 text-slate-700">Criticality Tier 4</Badge>
                      </TableCell>
                      <TableCell className="font-medium">Non-Critical</TableCell>
                      <TableCell className="text-center">72+ hours</TableCell>
                      <TableCell className="text-center">1 week</TableCell>
                      <TableCell className="text-sm">Rebuild / Manual recovery</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Strategy Options Tab */}
        <TabsContent value="options">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-amber-500" />
                Available Strategy Options
              </CardTitle>
              <CardDescription>
                ISO 22301 compliant recovery strategy options to evaluate
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {STRATEGY_TYPES.map((strategy) => {
                  const Icon = strategy.icon;
                  return (
                    <Card key={strategy.id} className={`border-2 hover:shadow-md transition-shadow ${strategy.color.split(' ')[0]} border-transparent hover:border-current`}>
                      <CardContent className="pt-6">
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${strategy.color}`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-slate-900">{strategy.name}</h3>
                            <p className="text-sm text-slate-600 mt-1">{strategy.description}</p>
                            <div className="flex items-center gap-4 mt-3 text-xs">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                RTO: {strategy.rto}
                              </span>
                              <span className="flex items-center gap-1">
                                <DollarSign className="w-3 h-3" />
                                {strategy.cost}
                              </span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cost-Benefit Analysis Tab */}
        <TabsContent value="comparison">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Scale className="w-5 h-5 text-sky-700" />
                Cost-Benefit Comparison Matrix
              </CardTitle>
              <CardDescription>
                Compare strategies across RTO capability, cost, and implementation complexity
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold">Strategy</TableHead>
                      <TableHead className="font-semibold text-center">RTO Capability</TableHead>
                      <TableHead className="font-semibold text-center">Initial Cost</TableHead>
                      <TableHead className="font-semibold text-center">Annual Cost</TableHead>
                      <TableHead className="font-semibold text-center">Complexity</TableHead>
                      <TableHead className="font-semibold text-center">Best For</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium">Hot Site</TableCell>
                      <TableCell className="text-center"><Badge className="bg-emerald-100 text-emerald-700">&lt; 1 hour</Badge></TableCell>
                      <TableCell className="text-center"><Badge className="bg-red-100 text-red-700">$$$$$</Badge></TableCell>
                      <TableCell className="text-center"><Badge className="bg-red-100 text-red-700">$$$$$</Badge></TableCell>
                      <TableCell className="text-center"><Badge className="bg-red-100 text-red-700">High</Badge></TableCell>
                      <TableCell className="text-sm">Mission-critical, zero downtime tolerance</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Warm Site</TableCell>
                      <TableCell className="text-center"><Badge className="bg-amber-100 text-amber-700">4-24 hours</Badge></TableCell>
                      <TableCell className="text-center"><Badge className="bg-amber-100 text-amber-700">$$$$</Badge></TableCell>
                      <TableCell className="text-center"><Badge className="bg-amber-100 text-amber-700">$$$</Badge></TableCell>
                      <TableCell className="text-center"><Badge className="bg-amber-100 text-amber-700">Medium</Badge></TableCell>
                      <TableCell className="text-sm">Critical processes, moderate RTO</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Cold Site</TableCell>
                      <TableCell className="text-center"><Badge className="bg-blue-100 text-blue-700">24-72 hours</Badge></TableCell>
                      <TableCell className="text-center"><Badge className="bg-blue-100 text-blue-700">$$</Badge></TableCell>
                      <TableCell className="text-center"><Badge className="bg-emerald-100 text-emerald-700">$</Badge></TableCell>
                      <TableCell className="text-center"><Badge className="bg-emerald-100 text-emerald-700">Low</Badge></TableCell>
                      <TableCell className="text-sm">Non-critical, longer RTO acceptable</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Cloud Recovery</TableCell>
                      <TableCell className="text-center"><Badge className="bg-emerald-100 text-emerald-700">1-4 hours</Badge></TableCell>
                      <TableCell className="text-center"><Badge className="bg-blue-100 text-blue-700">$$</Badge></TableCell>
                      <TableCell className="text-center"><Badge className="bg-amber-100 text-amber-700">$$$</Badge></TableCell>
                      <TableCell className="text-center"><Badge className="bg-amber-100 text-amber-700">Medium</Badge></TableCell>
                      <TableCell className="text-sm">IT systems, scalable workloads</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Remote Work</TableCell>
                      <TableCell className="text-center"><Badge className="bg-emerald-100 text-emerald-700">&lt; 4 hours</Badge></TableCell>
                      <TableCell className="text-center"><Badge className="bg-emerald-100 text-emerald-700">$</Badge></TableCell>
                      <TableCell className="text-center"><Badge className="bg-emerald-100 text-emerald-700">$</Badge></TableCell>
                      <TableCell className="text-center"><Badge className="bg-emerald-100 text-emerald-700">Low</Badge></TableCell>
                      <TableCell className="text-sm">Office-based, knowledge work</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Manual Workaround</TableCell>
                      <TableCell className="text-center"><Badge className="bg-emerald-100 text-emerald-700">Immediate</Badge></TableCell>
                      <TableCell className="text-center"><Badge className="bg-emerald-100 text-emerald-700">$</Badge></TableCell>
                      <TableCell className="text-center"><Badge className="bg-emerald-100 text-emerald-700">$</Badge></TableCell>
                      <TableCell className="text-center"><Badge className="bg-emerald-100 text-emerald-700">Low</Badge></TableCell>
                      <TableCell className="text-sm">Short-term, low-volume processes</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
              
              <div className="mt-6 p-4 bg-sky-50 rounded-lg">
                <h4 className="font-semibold text-sky-900 flex items-center gap-2">
                  <Calculator className="w-4 h-4" />
                  Selection Criteria
                </h4>
                <ul className="mt-2 text-sm text-indigo-800 space-y-1">
                  <li>• Match strategy RTO capability to BIA-defined RTO requirements</li>
                  <li>• Consider total cost of ownership (TCO) over 3-5 years</li>
                  <li>• Evaluate organizational capability to implement and maintain</li>
                  <li>• Document justification for audit compliance (ISO 22301 Clause 8.3)</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Strategy Dialog — TurboTax Progressive Disclosure */}
      <StepDialog
        open={showCreateDialog}
        onOpenChange={(open) => { setShowCreateDialog(open); if (!open) resetForm(); }}
        title="Define Recovery Strategy"
        currentStep={strategyStep}
        onStepChange={setStrategyStep}
        onSubmit={handleCreateStrategy}
        onCancel={() => setShowCreateDialog(false)}
        submitLabel={saving ? "Saving..." : "Create Strategy"}
        submitDisabled={saving || !formData.process_name || !formData.strategy_type}
        saving={saving}
        canAdvance={
          strategyStep === 0 ? !!formData.process_name && !!formData.strategy_type :
          true
        }
        steps={[
          { id: "process", title: "Process", subtitle: "Which business process does this strategy protect, and what type of recovery approach will you use?", isComplete: !!formData.process_name && !!formData.strategy_type },
          { id: "targets", title: "Targets", subtitle: "Define the Recovery Time and Recovery Point Objectives for this strategy.", isComplete: !!formData.rto_target || !!formData.rpo_target },
          { id: "cost", title: "Cost", subtitle: "Estimate the implementation and ongoing costs for this strategy.", isComplete: !!formData.cost_level },
          { id: "details", title: "Details", subtitle: "Provide a description, benefits, risks, and set the status.", isComplete: !!formData.description },
        ]}
      >
        {/* Step 0: Process & Type */}
        {strategyStep === 0 && (
          <div className="space-y-4" data-testid="step-process">
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Process Name *</Label>
              <Input
                value={formData.process_name}
                onChange={(e) => setFormData({ ...formData, process_name: e.target.value })}
                placeholder="e.g., Payment Processing, Customer Support"
                data-testid="process-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Strategy Type *</Label>
              <div className="space-y-2">
                {STRATEGY_TYPES.map((strategy) => (
                  <button
                    key={strategy.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, strategy_type: strategy.id })}
                    className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                      formData.strategy_type === strategy.id
                        ? 'border-sky-500 bg-sky-50 ring-1 ring-sky-200'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`strategy-type-${strategy.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <strategy.icon className="w-4 h-4" />
                      <span className="font-medium text-sm">{strategy.name}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 1: Targets */}
        {strategyStep === 1 && (
          <div className="space-y-4" data-testid="step-targets">
            <div className="space-y-2">
              <Label className="text-sm font-semibold">RTO Target</Label>
              <Input
                value={formData.rto_target}
                onChange={(e) => setFormData({ ...formData, rto_target: e.target.value })}
                placeholder="e.g., 4 hours"
                data-testid="rto-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">RPO Target</Label>
              <Input
                value={formData.rpo_target}
                onChange={(e) => setFormData({ ...formData, rpo_target: e.target.value })}
                placeholder="e.g., 1 hour"
                data-testid="rpo-input"
              />
            </div>
          </div>
        )}

        {/* Step 2: Cost */}
        {strategyStep === 2 && (
          <div className="space-y-4" data-testid="step-cost">
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Implementation Cost</Label>
              <Input
                value={formData.implementation_cost}
                onChange={(e) => setFormData({ ...formData, implementation_cost: e.target.value })}
                placeholder="e.g., $50,000"
                data-testid="impl-cost-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Annual Cost</Label>
              <Input
                value={formData.annual_cost}
                onChange={(e) => setFormData({ ...formData, annual_cost: e.target.value })}
                placeholder="e.g., $12,000"
                data-testid="annual-cost-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Cost Level</Label>
              <div className="space-y-2">
                {COST_LEVELS.map((level) => (
                  <button
                    key={level}
                    type="button"
                    onClick={() => setFormData({ ...formData, cost_level: level })}
                    className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                      formData.cost_level === level
                        ? 'border-sky-500 bg-sky-50 ring-1 ring-sky-200'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`cost-level-${level}`}
                  >
                    <p className="font-medium text-sm">{level}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Details */}
        {strategyStep === 3 && (
          <div className="space-y-4" data-testid="step-details">
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Strategy Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe the recovery strategy in detail..."
                rows={3}
                data-testid="description-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Key Benefits</Label>
              <Textarea
                value={formData.benefits}
                onChange={(e) => setFormData({ ...formData, benefits: e.target.value })}
                placeholder="List the key benefits of this strategy..."
                rows={2}
                data-testid="benefits-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Risks & Limitations</Label>
              <Textarea
                value={formData.risks}
                onChange={(e) => setFormData({ ...formData, risks: e.target.value })}
                placeholder="Document any risks or limitations..."
                rows={2}
                data-testid="risks-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((status) => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </StepDialog>

      {/* View Strategy Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Strategy Details</DialogTitle>
          </DialogHeader>
          {selectedStrategy && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500">Process</Label>
                  <p className="font-medium">{selectedStrategy.process_name}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Strategy Type</Label>
                  <p className="font-medium">{getStrategyInfo(selectedStrategy.strategy_type).name}</p>
                </div>
                <div>
                  <Label className="text-slate-500">RTO Target</Label>
                  <p className="font-medium">{selectedStrategy.rto_target || "-"}</p>
                </div>
                <div>
                  <Label className="text-slate-500">RPO Target</Label>
                  <p className="font-medium">{selectedStrategy.rpo_target || "-"}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Cost Level</Label>
                  <Badge variant="outline">{selectedStrategy.cost_level}</Badge>
                </div>
                <div>
                  <Label className="text-slate-500">Status</Label>
                  <Badge>{selectedStrategy.status}</Badge>
                </div>
              </div>
              {selectedStrategy.description && (
                <div>
                  <Label className="text-slate-500">Description</Label>
                  <p className="text-sm text-slate-700 mt-1">{selectedStrategy.description}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
